import React, { useState, useEffect } from 'react';
import { Spin, Empty, Table, Alert, Space, Button, Modal, Form, Input, DatePicker, TimePicker } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { baseUrl_SPB } from '../../../BaseUrl';
import axios from 'axios';
import dayjs from 'dayjs';

const UnavailabilityPanel = ({ employeeId, token, onUpdate }) => {
  const [unavailability, setUnavailability] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUnavailability();
  }, [employeeId]);

  const fetchUnavailability = async () => {
    if (!token || !employeeId) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(
        `${baseUrl_SPB}/api/v1/unavailability/getall?staffId=${employeeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error('Failed to load unavailability');
      const data = await response.json();
      setUnavailability(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUnavailability = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setError('');

      const payload = {
        id: null,
        type: 'U',
        staffId: employeeId,
        startDate: values.startDate.format('DD-MM-YYYY'),
        endDate: values.endDate.format('DD-MM-YYYY'),
        allDay: values.allDay || false,
        repeat: false,
        reason: values.reason || "Can't work",
        starttime: values.allDay ? '00:00' : (values.startTime?.format('HH:mm') || '00:00'),
        endtime: values.allDay ? '23:59' : (values.endTime?.format('HH:mm') || '23:59'),
        repeatSettings: null,
      };

      await axios.post(`${baseUrl_SPB}/api/v1/unavailability/save`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setModalOpen(false);
      form.resetFields();
      await fetchUnavailability();
      onUpdate?.();
    } catch (err) {
      if (err?.errorFields) return;
      setError('Failed to save unavailability.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Delete unavailability',
      content: 'Are you sure?',
      okText: 'Delete',
      cancelText: 'Cancel',
      danger: true,
      onOk: async () => {
        try {
          await axios.get(`${baseUrl_SPB}/api/v1/unavailability/delete?id=${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          await fetchUnavailability();
          onUpdate?.();
        } catch (err) {
          setError('Failed to delete unavailability.');
          console.error(err);
        }
      },
    });
  };

  const columns = [
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: (text) => text || "Can't work",
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
    },
    {
      title: 'Time',
      key: 'time',
      render: (_, record) => `${record.starttime} - ${record.endtime}`,
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record.id)}
        />
      ),
    },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={12}>
      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} />}
      
      <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
        Add unavailability
      </Button>

      <Spin spinning={loading}>
        {unavailability.length === 0 ? (
          <Empty description="No unavailability recorded" />
        ) : (
          <Table
            rowKey={(rec) => rec.id || rec.startDate}
            dataSource={unavailability}
            columns={columns}
            pagination={false}
            size="small"
          />
        )}
      </Spin>

      <Modal
        open={modalOpen}
        title="Add Unavailability"
        okText="Save"
        cancelText="Cancel"
        onOk={handleAddUnavailability}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
        }}
        confirmLoading={saving}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'Required' }]}
            initialValue="Can't work"
          >
            <Input placeholder="e.g. Sick leave, Personal, Vacation" />
          </Form.Item>

          <Form.Item
            name="startDate"
            label="Start date"
            rules={[{ required: true, message: 'Required' }]}
          >
            <DatePicker format="DD-MM-YYYY" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="endDate"
            label="End date"
            rules={[{ required: true, message: 'Required' }]}
          >
            <DatePicker format="DD-MM-YYYY" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="startTime"
            label="Start time"
          >
            <TimePicker format="HH:mm" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="endTime"
            label="End time"
          >
            <TimePicker format="HH:mm" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default UnavailabilityPanel;
